<template>
  <div v-if="competitorMaterials" class="h-full">
    <BaseTable
      :headers="competitorMaterialsHeaders"
      :list="competitorMaterials"
      :is-add-new-hidden="mode == 'view'"
      table-id="competitorMaterials"
      :total-records="competitorMaterialsTotalCount"
      is-download-button-hidden
      :hideMultiselectDelete="mode == 'edit' ? false : true"
      hide-grouping
      @refresh-data="onRefreshData"
      @add-new="onOpenModal"
      @tableItemClicked="onOpenModal"
      :clickable="true"
      @deleteMultiselect="onDeleteMultiselect">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>

    <MaterialDetailsCompetitorAlterCodesModal
      :competitor-alter-code="competitorAlterCode"
      :is-open="isModalOpen"
      :is-new="isNewAlterCode"
      :mode="modalMode"
      :sequence-list="sequenceList"
      @close="isModalOpen = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { getCompetitorMaterialsWithConditions } from '@/services/api/materials';
import { v4 as uuidv4 } from 'uuid';
import BaseTable from '../common/BaseTable.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import MaterialDetailsCompetitorAlterCodesModal from './MaterialDetailsCompetitorAlterCodesModal.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { CompetitorMaterial } from '@/types/Materials';
import { useI18n } from 'vue-i18n';

const { alterCodeTypes } = storeToRefs(useLookupsStore());

const props = defineProps<{
  mode: 'view' | 'edit';
  materialId: string;
  resetState: boolean;
  modifiedData: CompetitorMaterial[] | undefined;
}>();

const emit = defineEmits(['saveCompetitorMaterialAlterCode']);

const competitorMaterials = ref<CompetitorMaterial[]>([]);
const competitorMaterialsTotalCount = ref(0);

const { t: $t } = useI18n({ useScope: 'global' });
const competitorMaterialsHeaders = ref([
  { value: 'competitor.code', text: $t('material.competitor'), type: 'text', active: true },
  {
    value: 'materialAlterCode.alterCode.name',
    text: $t('material.alterCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.measurementUnit.name',
    text: $t('material.measurementUnit'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.relationFactor',
    text: $t('material.relationFactor'),
    type: 'number',
    active: true,
  },
  {
    value: 'materialAlterCode.sequence',
    text: $t('baseObject.sequence'),
    type: 'number',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
]);

function matchesQuery(item: CompetitorMaterial, query: string | undefined): boolean {
  if (!query) return true;
  return (
    item.competitor.code?.toLowerCase().includes(query.toLowerCase()) ||
    item.materialAlterCode?.alterCode?.code?.toLowerCase().includes(query.toLowerCase()) ||
    item.materialAlterCode?.alterCode?.name?.toLowerCase().includes(query.toLowerCase()) ||
    item.materialAlterCode?.measurementUnit?.name?.toLowerCase().includes(query.toLowerCase())
  );
}

async function onRefreshData(data: TableFilterData) {
  const page = data.page ?? 1;
  const itemsPerPage = data.itemsPerPage ?? 50;

  const propsData = props.modifiedData || [];
  const dataAdded = propsData.filter((m) => m.recordMode === RecordMode.Added);
  const dataModified = propsData.filter((m) => m.recordMode === RecordMode.Modified);
  const dataDeleted = propsData.filter((m) => m.recordMode === RecordMode.Deleted);

  // If there are search results in new items
  const existingDataWithQuery = dataAdded.filter((item) => matchesQuery(item, data.query));

  let itemsToFetch = itemsPerPage;
  if (page === 1) {
    itemsToFetch = Math.max(0, itemsPerPage);
  }

  const sortColumn =
    data.column !== undefined ? competitorMaterialsHeaders.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;

  const params = {
    page: data.page || 1,
    itemsPerPage: itemsToFetch,
    query: data.query || '',
    sortColumn,
    sortAscending,
    materialId: props.materialId,
  };
  const res = await getCompetitorMaterialsWithConditions(params);

  if (!res) {
    competitorMaterials.value = [];
    competitorMaterialsTotalCount.value = 0;
    return;
  }
  let result = res.data;

  // Remove deleted
  result = result.filter((item) => !dataDeleted.some((delItem) => delItem.id === item.id));

  // Update modified
  result = result.map((item) => {
    const modifiedItem = dataModified.find((x) => x.id === item.id);
    return modifiedItem ? modifiedItem : item;
  });

  // Add new items in page 1
  if (page === 1 && existingDataWithQuery.length) {
    result = [...existingDataWithQuery, ...result];
  }

  competitorMaterials.value = result;
  competitorMaterialsTotalCount.value = res.count;
}

const isModalOpen = ref(false);
const competitorAlterCode = ref<CompetitorMaterial>(initializeCompetitorAlterCode());
const isNewAlterCode = ref<boolean>(false);

const sequenceList = computed<number[]>(() => {
  return competitorMaterials.value
    .map((x) => x.materialAlterCode.sequence)
    .filter((n): n is number => n !== null && n !== undefined);
});

function initializeCompetitorAlterCode(): CompetitorMaterial {
  const alterCodeId = uuidv4();
  return {
    id: uuidv4(),
    isActive: true,
    isFromManager: true,
    isDefault: true,
    competitor: { id: '', code: '' },
    materialAlterCode: {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      alterCode: {
        id: alterCodeId,
        code: '',
        name: '',
        alterCodeTypeId: alterCodeTypes.value.find((type) => type.eType == 1)?.id || '',
        recordMode: RecordMode.Added,
      },
      alterCodeId,
      measurementUnitId: '',
      measurementUnit: { id: '', code: '', name: '' },
      materialId: props.materialId,
      recordMode: RecordMode.Added,
    },
    recordMode: RecordMode.Added,
  };
}

const modalMode = ref<'view' | 'edit'>('view');
function onOpenModal(item: CompetitorMaterial) {
  if (item) {
    isNewAlterCode.value = false;
    competitorAlterCode.value = item;
    competitorAlterCode.value.recordMode = RecordMode.Modified;
    if (competitorAlterCode.value.isFromManager && props.mode == 'edit') modalMode.value = 'edit';
    else modalMode.value = 'view';
  } else {
    isNewAlterCode.value = true;
    competitorAlterCode.value = initializeCompetitorAlterCode();
    modalMode.value = 'edit';
  }
  isModalOpen.value = true;
}

function onSave(material: CompetitorMaterial) {
  const index = competitorMaterials.value.findIndex((item) => item.id == material.id);

  if (material.recordMode === RecordMode.Deleted) {
    if (index !== -1 && index !== undefined && competitorMaterials.value) {
      competitorMaterials.value.splice(index, 1);
    } else {
      if (competitorMaterials.value) competitorMaterials.value.push(material);
    }
  } else {
    if (index !== -1 && index !== undefined && competitorMaterials.value) {
      competitorMaterials.value[index] = material;
    } else {
      if (competitorMaterials.value) competitorMaterials.value.push(material);
    }
  }

  emit('saveCompetitorMaterialAlterCode', [material]);
  isModalOpen.value = false;
  isNewAlterCode.value = false;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<CompetitorMaterial>();

function onDeleteRow(item: CompetitorMaterial) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  deleteRowItem.value.recordMode = RecordMode.Deleted;
  modalDeleteRow.value = false;
  onSave(deleteRowItem.value);
}

function onDeleteMultiselect(items: CompetitorMaterial[]) {
  items.forEach((item) => {
    if (item.isFromManager) {
      item.recordMode = RecordMode.Deleted;
    }
    onSave(item);
  });
}

watch(
  () => props.resetState,
  async (newData) => {
    if (newData) {
      competitorMaterials.value = [];
      await onRefreshData({
        page: 1,
        itemsPerPage: 50,
      });
    }
  }
);

onMounted(async () => {
  await onRefreshData({
    page: 1,
    itemsPerPage: 50,
  });
});
</script>
